<template>
    <div class="tracking-container">
        <div id="map-container">
            <l-map 
                ref="map" 
                :zoom="zoom" 
                :center="center" 
                :options="{zoomControl: false}" 
                style="height: 100%; width: 100%;"
                @update:zoom="zoomUpdated"
                @update:center="centerUpdated"
            >
                <l-tile-layer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    attribution="&copy; <a href='https://www.openstreetmap.org/copyright'>OpenStreetMap</a> contributors" />
                
                <l-geo-json v-if="processionRoute" :geojson="processionRoute" :options="geoJsonOptions"></l-geo-json>

                <!-- Marcador de inicio de ruta -->
                <l-marker v-if="startPoint" :lat-lng="startPoint">
                    <l-popup>Inicio del recorrido</l-popup>
                </l-marker>

                <!-- Marcador de fin de ruta -->
                <l-marker v-if="endPoint" :lat-lng="endPoint">
                    <l-popup>Fin del recorrido</l-popup>
                </l-marker>
                
                <!-- Flechas de dirección -->
                <l-marker v-for="(arrow, index) in routeArrows" :key="`arrow-${index}`" :lat-lng="arrow.latLng" :icon="arrow.icon">
                </l-marker>

                <!-- Marcador de ubicación en tiempo real (Rojo) -->
                <l-marker :lat-lng="markerPosition" :icon="redIcon">
                    <l-popup>
                        <div class="popup-content">
                            <strong>Ubicación Actual</strong>
                            <p>Procesión en ruta</p>
                            <small v-if="lastUpdated">Act: {{ lastUpdated }}</small>
                        </div>
                    </l-popup>
                </l-marker>
            </l-map>
        </div>

        <!-- Tarjeta Flotante de Información -->
        <transition name="slide-fade">
            <div class="info-card-overlay" v-if="processionInfo" :style="{ '--primary': hermandadColor, '--primary-light': hexToRgba(hermandadColor, 0.2) }">
                <div class="card-content">
                    <img v-if="hermandadLogo" :src="hermandadLogo" alt="Logo de la Hermandad" class="hermandad-logo" />
                    <h2 v-else class="hermandad-title">{{ processionInfo.hermandad }}</h2>
                    <h3 class="procession-title">{{ processionInfo.descripcion }}</h3>
                    <div class="status-badge" :style="{ backgroundColor: 'var(--primary-light)', color: 'var(--primary)' }" :class="{ 'inactive': !isTrackingActive }">
                        <span class="pulse-dot" v-if="isTrackingActive" :style="{ backgroundColor: 'var(--primary)' }"></span> 
                        {{ isTrackingActive ? 'En Recorrido' : 'Programada' }}
                    </div>
                    <p class="last-update" v-if="lastUpdated">
                        <!-- Clock SVG -->
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="14" height="14"><path d="M12,2A10,10 0 1,0 22,12,10,10 0 0,0 12,2M12,20A8,8 0 1,1 20,12,8,8 0 0,1 12,20M12.5,7H11V13L16.25,16.15L17,14.92L12.5,12.25V7Z" /></svg>
                        Última actualización: {{ lastUpdated }}
                    </p>
                </div>
            </div>
        </transition>

        <!-- Botón de Re-centrar -->
        <button class="recenter-btn" @click="reCenterMap" title="Centrar en procesión">
            <!-- Target/Crosshair SVG -->
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path :fill="hermandadColor" d="M12,8A4,4 0 1,0 16,12A4,4 0 0,0 12,8M12,14A2,2 0 1,1 14,12A2,2 0 0,1 12,14M13,0.05V2.05C17.94,2.56 21.44,6.05 21.95,11H23.95V13H21.95C21.44,17.94 17.94,21.44 13,21.95V23.95H11V21.95C6.06,21.44 2.56,17.94 2.05,13H0.05V11H2.05C2.56,6.05 6.06,2.56 11,2.05V0.05H13M12,4A8,8 0 0,0 4,12A8,8 0 0,0 12,20A8,8 0 0,0 20,12A8,8 0 0,0 12,4Z" /></svg>
        </button>
    </div>
</template>

<script>
import { LMap, LTileLayer, LMarker, LPopup, LGeoJson } from 'vue2-leaflet';
import 'leaflet/dist/leaflet.css';
import axios from 'axios';
import L from 'leaflet';
import { EncryptionService } from "../utils/encryption";

// Fix for default marker icon in Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

// Custom Red Icon for live position
const redIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-red.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/0.7.7/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

export default {
    name: 'RealTimeMap',
    components: {
        LMap,
        LTileLayer,
        LMarker,
        LPopup,
        LGeoJson
    },
    props: {
        processionId: {
            type: String,
            required: true
        }
    },
    data() {
        return {
            zoom: 16,
            center: [14.559183, -90.670203],
            markerPosition: [14.559183, -90.670203],
            token: null,
            pollingInterval: null,
            processionRoute: null,
            processionInfo: null,
            startPoint: null,
            endPoint: null,
            routeArrows: [],
            redIcon: redIcon,
            lastUpdated: null,
            isTrackingActive: false,
            hermandadColor: '#6A1B9A',
            hermandadLogo: null,
            geoJsonOptions: {
                style: () => {
                    return {
                        color: this.hermandadColor, 
                        weight: 6,
                        opacity: 0.8,
                        lineCap: 'round'
                    };
                }
            }
        };
    },
    async mounted() {
        if (this.processionId) {
            this.$cookies.set("ProcesionID", this.processionId);
        }
        
        // Initialize encryption service
        await EncryptionService.init();

        await this.authenticateService();
        
        if (this.token) {
            await this.fetchProcessionInfo();
            // Check schedule before starting tracking
            this.checkScheduleAndStart();
        }
    },
    beforeDestroy() {
        if (this.pollingInterval) {
            clearInterval(this.pollingInterval);
        }
    },
    methods: {
        checkScheduleAndStart() {
            if (!this.processionInfo || !this.processionInfo.fecha) {
                // If no date, assume active or handle as error? defaulting to active for safety
                console.warn("No date found, defaulting to active");
                this.fetchCoordinates();
                this.startTracking();
                return;
            }

            const scheduledDate = new Date(this.processionInfo.fecha);
            const now = new Date();

            if (now >= scheduledDate) {
                this.isTrackingActive = true;
                this.fetchCoordinates();
                this.startTracking();
            } else {
                this.isTrackingActive = false;
                // Center on start point if available
                if (this.startPoint && this.$refs.map && this.$refs.map.mapObject) {
                     this.$refs.map.mapObject.setView(this.startPoint, 16);
                }
            }
        },
        reCenterMap() {
            if (this.markerPosition && this.$refs.map && this.$refs.map.mapObject) {
                const map = this.$refs.map.mapObject;
                map.setView(this.markerPosition, 17, { animate: true });
                // We do NOT update this.center to avoid fighting with Leaflet's internal state
            }
        },
        calculateBearing(startLat, startLng, destLat, destLng) {
            const startLatRad = this.toRadians(startLat);
            const startLngRad = this.toRadians(startLng);
            const destLatRad = this.toRadians(destLat);
            const destLngRad = this.toRadians(destLng);

            const y = Math.sin(destLngRad - startLngRad) * Math.cos(destLatRad);
            const x = Math.cos(startLatRad) * Math.sin(destLatRad) -
                      Math.sin(startLatRad) * Math.cos(destLatRad) * Math.cos(destLngRad - startLngRad);
            
            let brng = Math.atan2(y, x);
            brng = this.toDegrees(brng);
            return (brng + 360) % 360;
        },
        toRadians(degrees) {
            return degrees * Math.PI / 180;
        },
        toDegrees(radians) {
            return radians * 180 / Math.PI;
        },
        generateArrows(coordinates) {
            const arrows = [];
            // Increase density: Show more arrows (e.g., ~30 per route)
            const step = Math.max(1, Math.floor(coordinates.length / 30)); 
            
            for (let i = 0; i < coordinates.length - 1; i += step) {
                const start = coordinates[i]; // [lng, lat]
                // Look ahead a bit to get a stable bearing if points are very close
                const end = coordinates[Math.min(i + 2, coordinates.length - 1)]; 
                
                if (start[0] === end[0] && start[1] === end[1]) continue;

                const bearing = this.calculateBearing(start[1], start[0], end[1], end[0]);
                
                // Correction: ➤ points Right (90deg). North is 0deg.
                // We need to rotate it so it points North when bearing is 0.
                // Right (90) -> North (0) requires -90 rotation.
                const rotation = bearing - 90;

                const arrowIcon = L.divIcon({
                    className: 'arrow-icon',
                    html: `<div style="transform: rotate(${rotation}deg); font-size: 24px; color: ${this.hermandadColor}; text-shadow: 1px 1px 2px white;">➤</div>`,
                    iconSize: [24, 24],
                    iconAnchor: [12, 12] // Center of the 24x24 icon
                });

                arrows.push({
                    latLng: [start[1], start[0]],
                    icon: arrowIcon
                });
            }
            this.routeArrows = arrows;
        },
        async authenticateService() {
            try {
                const loginUrl = "https://wm1xvvtarj.execute-api.us-east-1.amazonaws.com/dev/login";
                
                const encryptedPassword = await EncryptionService.encrypt("Prueba123-");
                const encryptedUsername = await EncryptionService.encrypt("jmacajabon");
                
                if (!encryptedPassword || !encryptedUsername) {
                     console.error("Error al cifrar credenciales de servicio.");
                     return;
                }

                const response = await axios.post(loginUrl, {
                    username: encryptedUsername,
                    password: encryptedPassword
                });

                if (response.data.status === "Ok") {
                    this.token = response.data.jwtToken;
                }
            } catch (error) {
                console.error("Error al autenticar servicio:", error);
            }
        },
        async fetchProcessionInfo() {
            if (!this.token || !this.processionId) return;

            try {
                const apiUrl = `https://w9k3pnak7d.execute-api.us-east-1.amazonaws.com/dev/procesion/${this.processionId}/info`;
                const response = await axios.get(apiUrl, {
                    headers: { Authorization: `Bearer ${this.token}` }
                });

                if (response.data) {
                    this.processionInfo = {
                        descripcion: response.data.descripcion,
                        hermandad: response.data.hermandad,
                        fecha: response.data.fecha // Store date
                    };
                    
                    if (response.data.Color_Hex) {
                        this.hermandadColor = response.data.Color_Hex;
                        // Setup global CSS variables for dynamic pulse effect
                        document.documentElement.style.setProperty('--track-primary', this.hermandadColor);
                        document.documentElement.style.setProperty('--track-primary-rgba', this.hexToRgba(this.hermandadColor, 0.7));
                    } else {
                        document.documentElement.style.setProperty('--track-primary', '#6A1B9A');
                        document.documentElement.style.setProperty('--track-primary-rgba', 'rgba(74, 20, 140, 0.7)');
                    }
                    
                    if (response.data.logo) {
                        this.hermandadLogo = response.data.logo;
                    }

                    if (response.data.route) {
                        this.processionRoute = response.data.route;
                        
                        // Extract logic
                        if (this.processionRoute.features && this.processionRoute.features.length > 0) {
                            const geometry = this.processionRoute.features[0].geometry;
                            if (geometry.type === 'LineString') {
                                const coordinates = geometry.coordinates;
                                if (coordinates && coordinates.length > 0) {
                                    const start = coordinates[0];
                                    const end = coordinates[coordinates.length - 1];
                                    
                                    this.startPoint = [start[1], start[0]];
                                    this.endPoint = [end[1], end[0]];
                                    // Initial center on start point
                                    this.center = this.startPoint;

                                    // Generate Arrows
                                    this.generateArrows(coordinates);
                                }
                            }
                        }
                    }
                }
            } catch (error) {
                console.error("Error al obtener información:", error);
            }
        },
        async fetchCoordinates() {
            if (!this.token || !this.processionId) return;

            try {
                const apiUrl = `https://w9k3pnak7d.execute-api.us-east-1.amazonaws.com/dev/procesion/${this.processionId}/ubicacion`;
                
                const response = await axios.get(apiUrl, {
                    headers: { Authorization: `Bearer ${this.token}` }
                });

                if (response.data && response.data.latest) {
                    const { lat, lng } = response.data.latest;
                    if (lat && lng) {
                        this.markerPosition = [lat, lng];
                        this.lastUpdated = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
                        
                        // Update map center WITHOUT changing zoom
                        if (this.$refs.map && this.$refs.map.mapObject) {
                            const map = this.$refs.map.mapObject;
                            // Use panTo to smoothly move to new location, keeping current zoom
                            map.panTo([lat, lng], { animate: true });
                        }
                    }
                }
            } catch (error) {
                if (error.response && error.response.status === 401) {
                    await this.authenticateService();
                }
            }
        },
        startTracking() {
            this.pollingInterval = setInterval(() => {
                this.fetchCoordinates();
            }, 300000); // 5 minutos
        },
        zoomUpdated(zoom) {
            this.zoom = zoom;
        },
        centerUpdated(center) {
            this.center = center;
        },
        hexToRgba(hex, alpha = 1) {
            let color = hex;
            if(!color || !color.startsWith('#')) return `rgba(106, 27, 154, ${alpha})`;
            let r = parseInt(color.slice(1, 3), 16),
                g = parseInt(color.slice(3, 5), 16),
                b = parseInt(color.slice(5, 7), 16);
            return `rgba(${r}, ${g}, ${b}, ${alpha})`;
        }
    },
};
</script>

<style scoped>
.tracking-container {
    position: relative;
    height: 100vh;
    width: 100%;
    overflow: hidden;
}

#map-container {
    height: 100%;
    width: 100%;
    z-index: 1;
}

/* Tarjeta Flotante */
.info-card-overlay {
    position: absolute;
    top: 20px;
    left: 20px;
    width: 320px;
    background: rgba(255, 255, 255, 0.95);
    backdrop-filter: blur(8px);
    border-radius: 16px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
    z-index: 1000;
    padding: 20px;
    transition: all 0.3s ease;
}

.hermandad-title {
    font-size: 1.1rem;
    color: var(--primary);
    margin: 0 0 5px 0;
    font-weight: 700;
}

.hermandad-logo {
    max-width: 140px;
    max-height: 80px;
    display: block;
    margin: 0 auto 10px;
}

.procession-title {
    font-size: 0.95rem;
    color: #555;
    margin: 0 0 12px 0;
    font-weight: 500;
}

.status-badge {
    display: inline-flex;
    align-items: center;
    padding: 4px 10px;
    border-radius: 12px;
    font-size: 0.8rem;
    font-weight: 600;
    margin-bottom: 8px;
}

.pulse-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    margin-right: 6px;
    animation: pulse 2s infinite;
}

@keyframes pulse {
    0% { transform: scale(0.95); box-shadow: 0 0 0 0 var(--track-primary-rgba); }
    70% { transform: scale(1); box-shadow: 0 0 0 6px rgba(255, 255, 255, 0); }
    100% { transform: scale(0.95); box-shadow: 0 0 0 0 rgba(255, 255, 255, 0); }
}

.last-update {
    display: flex;
    align-items: center;
    font-size: 0.75rem;
    color: #777;
    margin: 5px 0 0 0;
}

.last-update svg {
    margin-right: 5px;
    fill: #777;
}

/* Botón de Re-centrar */
.recenter-btn {
    position: absolute;
    bottom: 30px;
    right: 20px;
    width: 56px;
    height: 56px;
    border-radius: 50%;
    background: #ffffff;
    border: none;
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
    display: flex;
    justify-content: center;
    align-items: center;
    cursor: pointer;
    z-index: 1000;
    transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
}

.recenter-btn:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.25);
    background-color: #f8f9fa;
}

.recenter-btn:active {
    transform: scale(0.95);
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.15);
}

.recenter-btn svg {
    width: 28px;
    height: 28px;
    fill: #4A148C; /* Color secundario */
}

/* Responsive Mobile Style */
@media (max-width: 768px) {
    .info-card-overlay {
        top: auto;
        bottom: 0;
        left: 0;
        width: 100%;
        border-radius: 24px 24px 0 0;
        padding-bottom: 30px; /* Safe area for swipe bar usually */
        box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.1);
    }
    
    .recenter-btn {
        bottom: 200px; /* Subirlo para que no quede tapado por la tarjeta */
    }
}

.slide-fade-enter-active, .slide-fade-leave-active {
  transition: all 0.3s ease;
}
.slide-fade-enter, .slide-fade-leave-to {
  transform: translateY(10px);
  opacity: 0;
}

.popup-content {
    text-align: center;
}
</style>